import { useState } from 'react';
import { useAppStore } from '../../services/store';
import type { App } from '../../services/types';
import EditAppModal from './EditAppModal';

interface AppManagementProps {
  filter?: string;
}

export default function AppManagement({ filter = 'all' }: AppManagementProps) {
  const { apps, setApps } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const categories = ['All', 'Productivity', 'Monitoring', 'Governance', 'Analytics', 'Citizen Services', 'AI Tools', 'Integrations', 'Communication'];

  const filteredApps = apps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleEditApp = (app: App) => {
    setEditingApp(app);
    setShowEditModal(true);
  };

  const handleDeleteApp = (appId: string) => {
    if (confirm('Are you sure you want to delete this app? This action cannot be undone.')) {
      const updatedApps = apps.filter(app => app.id !== appId);
      setApps(updatedApps);
    }
  };

  const toggleAppStatus = (appId: string) => {
    const updatedApps = apps.map(app =>
      app.id === appId
        ? { ...app, isActive: !app.isActive }
        : app
    );
    setApps(updatedApps);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Search apps by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              App Management ({filteredApps.length} apps)
            </h3>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredApps.map((app) => (
            <div key={app.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={app.coverImage}
                    alt={app.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">{app.name}</h4>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{app.description}</p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <i className="ri-folder-line mr-1"></i>
                          {app.category}
                        </span>
                        <span className="flex items-center">
                          <i className="ri-star-fill mr-1 text-yellow-400"></i>
                          {app.rating} ({app.reviewCount} reviews)
                        </span>
                        <span className="flex items-center">
                          <i className="ri-download-line mr-1"></i>
                          {app.downloads} installs
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          app.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {app.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => toggleAppStatus(app.id)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          app.isActive
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {app.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleEditApp(app)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteApp(app.id)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredApps.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-apps-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No apps found</h3>
            <p className="text-gray-600">Try adjusting your search terms or filters</p>
          </div>
        )}
      </div>

      {showEditModal && editingApp && (
        <EditAppModal
          app={editingApp}
          onClose={() => {
            setShowEditModal(false);
            setEditingApp(null);
          }}
        />
      )}
    </div>
  );
}
